---
id: apps-app-react-auth-site-layouts-doc
title: App React Auth Site Layouts
owner: Luc LEMEREZ
status: Active
lastReviewed: 2026-02-19
visibility: internal
tags:
  - app
  - auth
  - frontend
  - layout
  - astro
---

## Product Overview

### Who it serves

This layouts module serves users in Rezi authentication and account-recovery
flows by providing a consistent page shell across all auth routes.

### Primary workflow

`AuthLayout.astro` wraps each auth page with shared branding, responsive
two-panel structure, and common behavior for login, signup, email confirmation,
and password reset journeys.

The left panel renders route-specific forms via `<slot />`, while the right
panel shows contextual onboarding visuals (`dashboard` or
`resume-skeleton`) depending on route intent.

### Business rules

The layout preserves redirect and UX query context (`redirect`, `redirectTo`,
`next`, `returnTo`, `language`, `theme`) to keep post-auth navigation stable.
It enforces OTP-driven verification for email confirmation and reset-password
completion, and redirects users out of invalid OTP states (for example, missing
email context).

For `/signup-resume`, the layout validates anonymous resume tokens before
loading a preview so only valid, time-bounded resume data can be displayed.

### Outcomes

Auth routes share one behavior contract for routing, tracking, OTP handling,
and auth integration, reducing flow drift between pages and keeping handoff to
`/dashboard/resumes` predictable after successful authentication.

### Out of scope

This folder does not own backend auth policy decisions, API authorization
rules, or authenticated dashboard feature logic.

## Technical Overview

### Architecture flow

`apps/app-react-auth-site/src/layouts/AuthLayout.astro` is the central
orchestrator. It composes global styles, page chrome, right-panel preview
components from `apps/app-react-auth-site/src/_components/auth`, and a single
inline runtime script that wires auth, OTP, redirects, and third-party
integrations.

### Entry points

Auth pages under `apps/app-react-auth-site/src/pages` import this layout and
supply metadata (`title`, `heading`, `description`) plus optional
`rightPanelVariant`. Data attributes on page forms/elements are consumed by the
layout script to attach behavior.

### Key modules and flows

The layout dynamically imports Firebase Auth SDK modules for email/password and
social auth (Google), with popup-first and redirect fallback logic.
OTP and password-reset actions call API Gateway endpoints (`v1/otp/send`,
`v1/otp/check`, `v1/auth/confirm-email`, `v1/new_password`) and attach
reCAPTCHA Enterprise tokens via `X-Recaptcha`.

For anonymous resume signup, token verification is handled via a Firebase Cloud
Function (`verify_anon_resume_token`), then Firestore `anon-resumes` data is
loaded and rendered into the preview template.

### Integrations and side effects

The layout initializes Intercom, GTM/GA, Amplitude, Bento, LinkedIn
Insight, Rewardful, ProfitWell, and Churnkey. It stores short-lived UX state
in `sessionStorage` and cookies (email draft, social redirect intent,
anonymous resume id, reset token).

Traffic for auth pages is edge-routed by the Cloudflare Worker `astro-auth`,
which proxies auth routes/assets to Netlify and falls back to the main app
origin for non-auth requests; this layout is the runtime surface served for
those proxied auth routes.

### Constraints and risks

Because nearly all auth client behavior lives in one file, regressions in
`AuthLayout.astro` can impact all authentication routes simultaneously.
Behavior is also sensitive to runtime env config (`API_GATEWAY`, Firebase public
keys, reCAPTCHA keys) and third-party script availability.

## Changelog

- 2026-02-19: Initial layouts documentation for `apps/app-react-auth-site/src/layouts`.
